// Utility function to convert date from numbers to Hindi words
// Example: "31/01/2010" -> "इकतीस जनवरी दो हज़ार दस"

const hindiNumbers = [
  '', 'एक', 'दो', 'तीन', 'चार', 'पांच', 'छह', 'सात', 'आठ', 'नौ', 'दस',
  'ग्यारह', 'बारह', 'तेरह', 'चौदह', 'पंद्रह', 'सोलह', 'सत्रह', 'अठारह', 'उन्नीस', 'बीस',
  'इक्कीस', 'बाईस', 'तेईस', 'चौबीस', 'पच्चीस', 'छब्बीस', 'सत्ताईस', 'अट्ठाईस', 'उनतीस', 'तीस', 'इकतीस',
  'बत्तीस', 'तैंतीस', 'चौंतीस', 'पैंतीस', 'छत्तीस', 'सैंतीस', 'अड़तीस', 'उनतालीस', 'चालीस', 'इकतालीस',
  'बयालीस', 'तैंतालीस', 'चौवालीस', 'पैंतालीस', 'छियालीस', 'सैंतालीस', 'अड़तालीस', 'उनचास', 'पचास', 'इक्यावन',
  'बावन', 'तिरेपन', 'चौवन', 'पचपन', 'छप्पन', 'सत्तावन', 'अठावन', 'उनसठ', 'साठ', 'इकसठ',
  'बासठ', 'तिरसठ', 'चौंसठ', 'पैंसठ', 'छियासठ', 'सड़सठ', 'अड़सठ', 'उनहत्तर', 'सत्तर', 'इकहत्तर',
  'बहत्तर', 'तिहत्तर', 'चौहत्तर', 'पचहत्तर', 'छिहत्तर', 'सतहत्तर', 'अठहत्तर', 'उनासी', 'अस्सी', 'इक्यासी',
  'बयासी', 'तिरासी', 'चौरासी', 'पचासी', 'छियासी', 'सतासी', 'अठासी', 'नवासी', 'नब्बे', 'इक्यानवे',
  'बानवे', 'तिरानवे', 'चौरानवे', 'पचानवे', 'छियानवे', 'सतानवे', 'अठानवे', 'निन्यानवे', 'निन्यानवे'
];

const hindiMonths = [
  '', 'जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून',
  'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'
];

const hindiTens = [
  '', 'दस', 'बीस', 'तीस', 'चालीस', 'पचास', 'साठ', 'सत्तर', 'अस्सी', 'नब्बे'
];

const hindiHundreds = 'सौ';
const hindiThousand = 'हज़ार';
const hindiLakh = 'लाख';
const hindiCrore = 'करोड़';

// Helper function to convert a number (up to 99) to Hindi words
function convertTwoDigitNumberToHindi(num: number): string {
  if (num === 0) return '';
  if (num <= 31) return hindiNumbers[num]; // For days, we have direct mapping up to 31
  if (num < 20) return hindiNumbers[num];
  const tens = Math.floor(num / 10);
  const units = num % 10;
  return hindiTens[tens] + (units > 0 ? ' ' + hindiNumbers[units] : '');
}

// Helper function to convert a number (up to 999) to Hindi words
function convertThreeDigitNumberToHindi(num: number): string {
  if (num === 0) return '';
  const hundreds = Math.floor(num / 100);
  const remainder = num % 100;
  let result = '';
  if (hundreds > 0) {
    result += hindiNumbers[hundreds] + ' ' + hindiHundreds;
  }
  if (remainder > 0) {
    result += (result ? ' ' : '') + convertTwoDigitNumberToHindi(remainder);
  }
  return result;
}

// Main function to convert a number to Hindi words (for years, up to crores)
function convertNumberToHindiWords(num: number): string {
  if (num === 0) return 'शून्य';
  let result = '';
  let tempNum = num;

  const crore = Math.floor(tempNum / 10000000);
  tempNum %= 10000000;
  const lakh = Math.floor(tempNum / 100000);
  tempNum %= 100000;
  const thousand = Math.floor(tempNum / 1000);
  tempNum %= 1000;
  const remainder = tempNum;

  if (crore > 0) {
    result += convertNumberToHindiWords(crore) + ' ' + hindiCrore;
  }
  if (lakh > 0) {
    result += (result ? ' ' : '') + convertTwoDigitNumberToHindi(lakh) + ' ' + hindiLakh;
  }
  if (thousand > 0) {
    result += (result ? ' ' : '') + convertTwoDigitNumberToHindi(thousand) + ' ' + hindiThousand;
  }
  if (remainder > 0) {
    result += (result ? ' ' : '') + convertThreeDigitNumberToHindi(remainder);
  }

  return result.trim();
}

// Special function to convert years before 2000 in "unnis so..." format
function convertYearToHindiWords(year: number): string {
  if (year >= 2000) {
    // Use existing logic for years 2000 and above
    return convertNumberToHindiWords(year);
  } else if (year >= 1900 && year < 2000) {
    // Special format for years 1900-1999: "unnis so..."
    const remainder = year % 100; // 47, 65, 77, 99, etc.
    
    let result = 'उन्नीस सौ';
    
    // Add remainder if it's not 00, using direct mapping from hindiNumbers array
    if (remainder > 0) {
      result += ' ' + hindiNumbers[remainder];
    }
    
    return result.trim();
  } else {
    // For years before 1900, use regular conversion
    return convertNumberToHindiWords(year);
  }
}

export function convertDateToHindiWords(dateString: string): string {
  if (!dateString) return '';

  // Handle different date formats
  let day: number, month: number, year: number;
  
  if (dateString.includes('/')) {
    // Format: DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length !== 3) {
      return '';
    }
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else if (dateString.includes('-')) {
    // Format: YYYY-MM-DD
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      return '';
    }
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else {
    return '';
  }

  if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31 || month < 1 || month > 12) {
    return '';
  }

  const dayInWords = hindiNumbers[day];
  const monthInWords = hindiMonths[month];
  const yearInWords = convertYearToHindiWords(year);

  return `${dayInWords} ${monthInWords} ${yearInWords}`.trim();
}

// Helper function to format date from form state to DD/MM/YYYY format
export function formatDateFromForm(dateObj: { day: string; month: string; year: string }): string {
  if (!dateObj.day || !dateObj.month || !dateObj.year) return '';
  return `${dateObj.day}/${dateObj.month}/${dateObj.year}`;
}

// English date conversion utilities
const englishMonths = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const englishNumbers = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty',
  'Twenty-One', 'Twenty-Two', 'Twenty-Three', 'Twenty-Four', 'Twenty-Five', 'Twenty-Six', 'Twenty-Seven', 'Twenty-Eight', 'Twenty-Nine', 'Thirty',
  'Thirty-One'
];

function convertYearToEnglishWords(year: number): string {
  if (year >= 2000) {
    // For years 2000-2099
    const remainder = year % 100;
    if (remainder === 0) {
      return 'Two Thousand';
    }
    return 'Two Thousand ' + englishNumbers[remainder];
  } else if (year >= 1900 && year < 2000) {
    // For years 1900-1999
    const remainder = year % 100;
    if (remainder === 0) {
      return 'Nineteen Hundred';
    }
    return 'Nineteen Hundred ' + englishNumbers[remainder];
  }
  return '';
}

export function convertDateToEnglishWords(dateString: string): string {
  if (!dateString) return '';

  // Handle different date formats
  let day: number, month: number, year: number;
  
  if (dateString.includes('/')) {
    // Format: DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length !== 3) {
      return '';
    }
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else if (dateString.includes('-')) {
    // Format: YYYY-MM-DD
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      return '';
    }
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else {
    return '';
  }

  if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31 || month < 1 || month > 12) {
    return '';
  }

  const dayInWords = englishNumbers[day];
  const monthInWords = englishMonths[month];
  const yearInWords = convertYearToEnglishWords(year);

  return `${dayInWords} ${monthInWords} ${yearInWords}`.trim();
}